/**
 * /settings/chat (v11, admin-style) — Configuração do chat fiel ao TiFlux:
 * breadcrumb "Configurações / Geral / Chat", aba única Geral, card "Chat" com
 * o toggle "Utilizar chat" + nossos campos (Mesa destino, Título do widget,
 * Mensagem de boas-vindas) e o bloco "Incorporar no seu site" com snippet
 * <script>/<iframe> copiável apontando para a rota pública /chat do SPA.
 */
import { useState, type FormEvent } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CheckCircle2, Copy, ShieldAlert } from 'lucide-react';
import type { ChatSettings } from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { api, type ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { PageSpinner } from '@/components/ui/misc';
import { useTransientToast } from '@/components/ui/transient-toast';
import { copyText } from '@/features/vault/vault-clipboard';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { SettingsBreadcrumb } from '@/features/settings/settings-ui';
import { CloseReasonsSection } from '@/features/chats/close-reasons-section';

const CHAT_SETTINGS_KEY = ['erp', 'chat', 'settings'] as const;

export function chatSettingsQuery() {
  return queryOptions({
    queryKey: CHAT_SETTINGS_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<ChatSettings>('/chat/settings', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

function errorMessage(e: unknown): string {
  const err = e as Partial<ApiError> | null;
  if (err?.error === 'validation_error') return 'Dados inválidos — confira os campos.';
  if (err?.status === 0) return 'Sem conexão com o servidor.';
  return err?.error ?? 'Erro inesperado.';
}

export function ChatSettingsPage() {
  const user = useAuthUser();
  if (user?.role !== 'admin') {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border bg-card text-center shadow-xs">
        <ShieldAlert className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">Acesso restrito</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          O widget de chat é configurado por administradores da conta.
        </p>
      </div>
    );
  }
  return <ChatSettingsAdmin />;
}

function ChatSettingsAdmin() {
  const settingsQ = useQuery(chatSettingsQuery());

  return (
    <div className="rounded-lg border bg-card shadow-xs">
      <div className="px-4 pt-3 sm:px-5">
        <SettingsBreadcrumb section="Geral" page="Chat" />
        <h1 className="mt-2 text-xl leading-7 font-semibold">Configuração do chat</h1>
        <div className="mt-3 border-b">
          <span className="inline-block border-b-2 border-primary pb-2 text-sm font-medium text-primary">
            Geral
          </span>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        {settingsQ.isPending ? (
          <PageSpinner />
        ) : settingsQ.isError ? (
          <p className="py-10 text-center text-sm text-destructive">
            Erro ao carregar a configuração do chat: {errorMessage(settingsQ.error)}
          </p>
        ) : (
          <ChatSettingsForm settings={settingsQ.data} />
        )}
      </div>
    </div>
  );
}

function ChatSettingsForm({ settings }: { settings: ChatSettings }) {
  const qc = useQueryClient();
  const desksQ = useQuery(desksListQuery());
  const [enabled, setEnabled] = useState(settings.enabled);
  const [deskId, setDeskId] = useState<number | null>(settings.deskId);
  const [title, setTitle] = useState(settings.title ?? '');
  const [greeting, setGreeting] = useState(settings.greeting ?? '');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api<ChatSettings>('/chat/settings', {
        method: 'PUT',
        body: {
          enabled,
          deskId,
          title: title.trim() || null,
          greeting: greeting.trim() || null,
        },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(CHAT_SETTINGS_KEY, data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  const desks = (desksQ.data ?? []).filter((d) => d.active || d.id === deskId);

  return (
    <div className="space-y-4">
      {/* ── Card "Chat" (fiel à captura: título discreto + toggle) ───────── */}
      <section className="rounded-lg border shadow-xs">
        <div className="px-4 py-3">
          <h2 className="text-[15px] text-muted-foreground">Chat</h2>
        </div>
        <form onSubmit={submit} className="space-y-4 px-4 pb-4">
          <div className="space-y-1.5">
            <p className="text-sm font-semibold">Utilizar chat</p>
            <Switch
              id="chat-enabled"
              aria-label="Utilizar chat"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="chat-desk">Mesa destino</Label>
              <Select
                id="chat-desk"
                value={deskId !== null ? String(deskId) : ''}
                onChange={(e) => setDeskId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Sem mesa</option>
                {desks.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.displayName}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                Mesa que recebe os chats iniciados pelo widget.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="chat-title">Título do widget</Label>
              <Input
                id="chat-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Atendimento"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="chat-greeting">Mensagem de boas-vindas</Label>
            <Textarea
              id="chat-greeting"
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              placeholder="Olá! Como podemos ajudar?"
              className="min-h-20"
            />
            <p className="text-xs text-muted-foreground">
              Exibida no widget assim que o visitante inicia a conversa.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
            {saved ? (
              <span className="flex items-center gap-1 text-xs text-[#52C41A]">
                <CheckCircle2 className="size-3.5" />
                Configuração salva.
              </span>
            ) : null}
            {save.error ? (
              <span className="text-xs text-destructive" role="alert">
                {errorMessage(save.error)}
              </span>
            ) : null}
          </div>
        </form>
      </section>

      <EmbedSection tenantSlug={settings.tenantSlug} />

      {/* spec 013: encerrar exige motivo — a lista vive aqui */}
      <CloseReasonsSection />
    </div>
  );
}

/**
 * Snippets de incorporação — a página pública do widget é a rota /chat do
 * próprio SPA (sem token na URL: o widget cria a sessão no primeiro acesso e
 * guarda o token no localStorage do visitante). O `?tenant=<slug>` é
 * OBRIGATÓRIO: é como o widget anônimo aponta o tenant para a API (config e
 * start) — sem ele a página cai no fallback sem configuração.
 */
function EmbedSection({ tenantSlug }: { tenantSlug: string }) {
  const { toast, showToast } = useTransientToast('success');
  const widgetUrl = `${window.location.origin}/chat?tenant=${encodeURIComponent(tenantSlug)}`;

  const scriptSnippet = [
    '<script>',
    '  (function () {',
    "    var f = document.createElement('iframe');",
    `    f.src = '${widgetUrl}';`,
    "    f.title = 'Chat de atendimento';",
    "    f.style.cssText = 'position:fixed;bottom:16px;right:16px;width:380px;height:560px;max-height:calc(100vh - 32px);border:0;border-radius:12px;box-shadow:0 8px 30px rgba(0,0,0,.18);z-index:9999;';",
    '    document.body.appendChild(f);',
    '  })();',
    '</script>',
  ].join('\n');

  const iframeSnippet = `<iframe src="${widgetUrl}" title="Chat de atendimento" width="380" height="560" style="border:0;border-radius:12px;"></iframe>`;

  const copy = (value: string, label: string) => {
    void copyText(value).then((ok) =>
      showToast(ok ? `${label} copiado.` : 'Não foi possível copiar — selecione e copie manualmente.')
    );
  };

  return (
    <section className="rounded-lg border shadow-xs">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold">Incorporar no seu site</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Adicione o widget de chat às suas páginas com um dos snippets abaixo.
        </p>
      </div>
      <div className="space-y-4 p-4">
        <SnippetBlock
          label="Botão flutuante (script)"
          value={scriptSnippet}
          onCopy={() => copy(scriptSnippet, 'Script')}
        />
        <SnippetBlock
          label="Embutido na página (iframe)"
          value={iframeSnippet}
          onCopy={() => copy(iframeSnippet, 'Iframe')}
        />
        <p className="text-xs text-muted-foreground">
          O widget abre a página pública {widgetUrl} — com o chat desativado, ela deixa de
          iniciar novas conversas.
        </p>
      </div>
      {toast}
    </section>
  );
}

function SnippetBlock({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            onCopy();
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? <Check className="text-[#52C41A]" /> : <Copy />}
          Copiar
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-xs leading-relaxed whitespace-pre">
        {value}
      </pre>
    </div>
  );
}
